import React, { useEffect, useState } from 'react';
import { useSettings } from './SettingsContext';
import { Dimension } from './SettingsContext';
import i18next from 'i18next';
import { useToast } from '../Dashboard/ToastContext';
import { Box, Button, CircularProgress, Fab, Grid, IconButton, TextField, Typography } from '@mui/material';
import { AddOutlined, DeleteOutlined, LanguageOutlined, SquareFootOutlined } from '@mui/icons-material';

const GeneralSetting: React.FC = () => {
    const { settings, updateSettings, loading } = useSettings();
    const { success, error: showError } = useToast();
    const [localLanguage, setLocalLanguage] = useState('');
    const [dimensions, setDimensions] = useState<Dimension[]>([]);
    const [saving, setSaving] = useState(false);

    // Sync local state when context settings change (e.g. after API fetch)
    useEffect(() => {
        if (!settings) return;
        setLocalLanguage(settings.system_language);
        setDimensions([...settings.load_dimensions]);
    }, [settings]);

    if (loading || !settings) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress />
            </Box>
        );
    }
    const dimensionLabels = ['First Dimension', 'Second Dimension', 'Third Dimension'];
    const handleDimensionChange = (
        index: number,
        key: keyof Dimension,
        value: string
    ) => {
        const updated = [...dimensions];
        updated[index] = { ...updated[index], [key]: value };
        setDimensions(updated);
    };

    const handleDeleteDimension = (index: number) => {
        setDimensions((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        const normalizedDimensions = dimensions.map((dim, idx) => ({
            ...dim,
            backendIndex: dim.backendIndex ?? idx,
        }));

        setSaving(true);
        try {
            await updateSettings({
                system_language: localLanguage,
                load_dimensions: normalizedDimensions,
            });
            localStorage.setItem('system_language', localLanguage);
            i18next.changeLanguage(localLanguage);
            success(
                <>
                    <b>{i18next.t('common:toast.successTitle')}:</b>&nbsp;
                    {i18next.t('common:toast.saveSuccess')}
                </>
            );
        } catch (err) {
            showError(
                <>
                    <b>{i18next.t('common:toast.errorTitle')}:</b>&nbsp;
                    {i18next.t('common:toast.saveFailed')}
                </>
            );
        } finally {
            setSaving(false);
        }
    };

    const addDimension = () => {
        if (dimensions.length < 3) {
            setDimensions(prev => [
                ...prev,
                { dimension: '', unit: '', backendIndex: prev.length }
            ]);
        }
    };

    return (
        <Box>
            <Box display="flex" alignItems="center" sx={{ mt: 2, gap: 2, width: '100%' }}>
                <Box display="flex" alignItems="center" sx={{ flex: 1 }}>
                    <LanguageOutlined />
                    <Typography variant="subtitle1" sx={{ marginLeft: 1 }}>
                        {i18next.t('common:settings.language')}
                    </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <TextField
                        select
                        fullWidth
                        label={i18next.t('common:settings.language')}
                        value={localLanguage || 'de'}
                        onChange={(e) => setLocalLanguage(e.target.value)}
                        SelectProps={{
                            native: true,
                        }}
                    >
                        <option value="de">Deutsch</option>
                        <option value="en">English</option>
                    </TextField>
                </Box>
            </Box>

            <Box display="flex" alignItems="center" sx={{ mt: 2, gap: 2, width: '100%' }}>
                <Box display="flex" alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                    <SquareFootOutlined />
                    <Typography variant="subtitle1" sx={{ marginLeft: 1 }}>
                        {i18next.t('common:settings.dimension')}
                    </Typography>
                </Box>
                <Box display="flex" flexDirection="column" gap={2} sx={{ flex: 1 }}>
                    {dimensions.map((dim, idx) => (
                        <Box key={idx} display="flex" gap={2} alignItems="center" width="100%">
                            <TextField
                                label={dimensionLabels[idx] || `Dimension ${idx + 1}`}
                                value={dim.dimension}
                                onChange={(e) => handleDimensionChange(idx, 'dimension', e.target.value)}
                                fullWidth
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                label="Unit"
                                value={dim.unit}
                                onChange={(e) => handleDimensionChange(idx, 'unit', e.target.value)}
                                fullWidth
                                sx={{ flex: 1 }}
                            />
                            <IconButton onClick={() => handleDeleteDimension(idx)} sx={{ padding: 1 }}>
                                <DeleteOutlined />
                            </IconButton>
                        </Box>
                    ))}
                    {dimensions.length < 3 && (
                        <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
                            <Fab
                                color="primary"
                                size="small"
                                onClick={addDimension}
                                data-cy="add_dimension"
                                sx={{ mb: 2, mr: 1 }}
                            >
                                <AddOutlined style={{ color: 'white' }} />
                            </Fab>
                        </Grid>
                    )}
                </Box>
            </Box>

            <Box display="flex" justifyContent="flex-end" mt={3}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
                >
                    {saving ? i18next.t('common:settings.saving') : i18next.t('common:settings.save')}
                </Button>
            </Box>
        </Box>
    );
};

export default GeneralSetting;

